import type { SetCommissionPaidDatesRequest } from '@/dtos/Commissions/SetCommissionPaidDatesRequest'
import type { SetCommissionPaidDatesResponseItem } from '@/dtos/Commissions/SetCommissionPaidDatesResponse'
import { useSetCommissionPaidDates } from '@/hooks/useCommissions'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Textarea,
  useToast,
  VStack
} from '@chakra-ui/react'
import { DateFormControl, TextareaFormControl } from 'BrokerTsLib/src/components'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

interface FormValues {
  commissionPaidDate: Date | null
  contractNumbers: string
}

export const SetCommissionPaidDates: React.FC = () => {
  const [results, setResults] = useState<SetCommissionPaidDatesResponseItem[]>([])
  const [hardRunEnabled, setHardRunEnabled] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const contractNumbersRef = useRef<HTMLTextAreaElement>(null)
  const resultsTextareaRef = useRef<HTMLTextAreaElement>(null)
  const toast = useToast()

  const { control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      commissionPaidDate: null,
      contractNumbers: ''
    }
  })

  const { mutateAsync: setCommissionPaidDatesAsync } = useSetCommissionPaidDates()

  const parseContractNumbers = (text: string): string[] => {
    if (!text.trim()) return []
    
    const separators = /[\n,;]+/
    return text
      .split(separators)
      .map(num => num.trim())
      .filter(num => num.length > 0)
  }

  const onSubmit = async (data: FormValues, setDates: boolean) => {
    if (!data.commissionPaidDate) {
      toast({
        title: 'Chyba validace',
        description: 'Prosím vyberte datum odeslání poukázek',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    const contractNumbers = parseContractNumbers(data.contractNumbers)
    
    if (contractNumbers.length === 0) {
      toast({
        title: 'Chyba validace',
        description: 'Prosím zadejte alespoň jedno číslo smlouvy',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setIsProcessing(true)

    const request: SetCommissionPaidDatesRequest = {
      contractNumbers,
      commissionPaidDate: data.commissionPaidDate,
      setDates
    }

    try {
      const response = await setCommissionPaidDatesAsync(request)
      setResults(response)
      
      if (!setDates) {
        setHardRunEnabled(true)
      } else {
        toast({
          title: 'Nastavení dokončeno',
          description: `Úspěšně aktualizováno ${response.filter(r => r.success).length} smluv`,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      }
    } catch {
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se zpracovat data odeslání poukázek',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setIsProcessing(false)
    }
  }

  useEffect(() => {
    if (results.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [results])

  const successCount = results.filter(r => r.success).length
  const errorCount = results.filter(r => !r.success).length

  const contractNumbersValue = watch('contractNumbers')
  // Reset hardRunEnabled when contractNumbersValue changes
  useEffect(() => {
    if (contractNumbersValue) {
      setHardRunEnabled(false)
    }
  }, [contractNumbersValue])

  // Sync scroll between textareas
  const handleContractNumbersScroll = () => {
    if (contractNumbersRef.current && resultsTextareaRef.current) {
      const scrollPercentage = contractNumbersRef.current.scrollTop / 
        (contractNumbersRef.current.scrollHeight - contractNumbersRef.current.clientHeight)
      resultsTextareaRef.current.scrollTop = scrollPercentage * 
        (resultsTextareaRef.current.scrollHeight - resultsTextareaRef.current.clientHeight)
    }
  }

  const handleResultsScroll = () => {
    if (contractNumbersRef.current && resultsTextareaRef.current) {
      const scrollPercentage = resultsTextareaRef.current.scrollTop / 
        (resultsTextareaRef.current.scrollHeight - resultsTextareaRef.current.clientHeight)
      contractNumbersRef.current.scrollTop = scrollPercentage * 
        (contractNumbersRef.current.scrollHeight - contractNumbersRef.current.clientHeight)
    }
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Nastavení data odeslání poukázek</Heading>

        <Card w="full">
          <CardBody as={Flex} direction={{ base: 'column', lg: 'row' }} 
            gap={6} padding={6}>
            <VStack spacing={4} w="full" align="stretch">
              <DateFormControl
                control={control}
                name="commissionPaidDate"
                label="Datum odeslání poukázek"
                placeholder="Select date"
              />

              <Grid gap={4} gridTemplateColumns="1fr 1fr">
                <TextareaFormControl
                  control={control}
                  name="contractNumbers"
                  label="Čísla smluv"
                  helperText="Zadejte čísla smluv oddělená novými řádky"
                  helperTextPosition='top'
                  variant="flushed"
                  textareaProps={{
                    h: '50vh',
                    resize: 'none',
                    onScroll: handleContractNumbersScroll
                  }}
                  ref={contractNumbersRef}
                />
                <VStack pt={8}>
                  <HStack>
                    <Badge colorScheme="green" size="sm">{successCount} Úspěšné</Badge>
                    <Badge colorScheme="red" size="sm">{errorCount} Chyby</Badge>
                    <Badge colorScheme="blue" size="sm">{results.length} Celkem</Badge>
                  </HStack>
                  <Textarea 
                    h="50vh" 
                    resize="none" 
                    variant="flushed" 
                    disabled 
                    ref={resultsTextareaRef}
                    onScroll={handleResultsScroll}
                    value={results.map((result) => `${result.contractNumber} - ${result.message ? result.message : result.success ? 'OK' : 'Chyba'}`).join('\n')}>
                  </Textarea>
                </VStack>
              </Grid>

              <HStack spacing={4} justifyContent="flex-end">
                <Button
                  onClick={handleSubmit((data) => onSubmit(data, false))}
                  isLoading={isProcessing && !hardRunEnabled}
                  loadingText="Zpracovávání..."
                  isDisabled={isProcessing}
                >
                  Zobrazit náhled
                </Button>

                <Button
                  colorScheme="green"
                  onClick={handleSubmit((data) => onSubmit(data, true))}
                  isLoading={isProcessing && hardRunEnabled}
                  loadingText="Nastavování..."
                  isDisabled={!hardRunEnabled || isProcessing}
                >
                  Nastavit data odeslání poukázek
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  )
}